import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  GetUpdatedProfileRequestPayload,
  GetUserProfileRequestPayload,
  UserState,
} from './user-profile.payload';
import { UpdateUserProfilePhotoRequest } from '../../atlasguides-web-common/src/functions/users/update-user-profile-photo';
import { GuidePurchaseReceipt, UserPrimaryInfo, UserProfileWithPurchases } from '../../atlasguides-web-common/src/functions/users/dto/user-profile.dto';
import { getEnabledPlannedRoute, setEnabledPlannedRoute as setPlannedRouteEnabled, findPlannedRouteById, updatePlannedRouteInfoByTrail, checkNewTrailPointAllowed } from 'utils/plannedRoutesHelper';
import { PlannedRouteTrail } from 'atlasguides-web-common/src/functions/guides/dto/guide-geo.dto';
import { GuideGeoExt, PlannedRouteExt, PlannedRoutePinInfo } from 'commonTypes/GuideUse/guideGeo';
import { TrailPoint } from 'services/Navigation/RouteTree';
import { PlannedRoute } from 'atlasguides-web-common/src/functions/users/dto/planned-route.dto';
import { GeoRouteBuilder } from 'services/Navigation/GeoRouteBuilder/GeoRouteBuilder';
export * from './user-profile.payload';


const initialState: UserState = {
  isLoading: false,
  userProfile: null,
  isUploadingProfileImage: false,
  isUploadingCoverImage: false,
  plannedRoutes: null,
  editingPlannedRoute: null,
  isLoadingPlannedRoutes: false,
  isSavingPlannedRoutes: false,
  isSignedIn: false,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    getUserProfileRequest: (
      state,
      action: PayloadAction<GetUserProfileRequestPayload>
    ) => {
      state.isLoading = true;
    },
    getUserProfileResponse: (
      state,
      action: PayloadAction<UserProfileWithPurchases>
    ) => {
      state.userProfile = action.payload;
      state.isLoading = false;
    },
    getUserProfileFail: (state, action: PayloadAction<void>) => {
      state.isLoading = false;
    },
    clearUserProfile: (state, action: PayloadAction<void>) => {
      state.userProfile = null;
      state.plannedRoutes = null;
      state.isLoading = false;
      state.isUploadingProfileImage = false;
      state.isUploadingCoverImage = false;
      state.isLoadingPlannedRoutes = false;
    },
    getUpdatedProfileRequest: (
      state,
      action: PayloadAction<GetUpdatedProfileRequestPayload>
    ) => state,
    getUpdatedProfileResponse: (
      state,
      action: PayloadAction<Partial<UserPrimaryInfo>>
    ) => {
      if (state.userProfile) {
        state.userProfile.primaryInfo = {
          ...state.userProfile.primaryInfo,
          ...action.payload,
        };
      }
      state.isLoading = false;
    },
    updateUserImageProfileRequest: (
      state,
      action: PayloadAction<UpdateUserProfilePhotoRequest>
    ) => {
      state.isUploadingProfileImage = true;
    },
    updateUserImageProfile: (
      state,
      action: PayloadAction<{ imageUrl: string }>
    ) => {
      if (state.userProfile) {
        state.userProfile.photos.profilePhotoUrl = action.payload.imageUrl;
        state.isUploadingProfileImage = false;
      }
    },
    updateUserImageProfileFail: (state, action: PayloadAction<void>) => {
      state.isUploadingProfileImage = false;
    },
    updateUserCoverImageRequest: (
      state,
      action: PayloadAction<UpdateUserProfilePhotoRequest>
    ) => {
      state.isUploadingCoverImage = true;
    },
    updateUserCoverImage: (
      state,
      action: PayloadAction<{ imageUrl: string }>
    ) => {
      if (state.userProfile) {
        state.userProfile.photos.profileCoverImageUrl = action.payload.imageUrl;
        state.isUploadingCoverImage = false;
      }
    },
    updateUserCoverImageFail: (state, action: PayloadAction<void>) => {
      state.isUploadingCoverImage = false;
    },
    addPurchasedItems: (
      state,
      action: PayloadAction<{ purchasedItems: GuidePurchaseReceipt[] }>
    ) => {
      if (state.userProfile) {
        state.userProfile.purchases = [
          ...action.payload.purchasedItems,
          ...(state.userProfile?.purchases || []),
        ];
      }
    },
    // getPlannedRouteRequest: (
    //   state,
    //   action: PayloadAction<GetPlannedRoutesRequest>
    // ) => {
    //   state.isLoadingPlannedRoutes = true;
    // },
    getPlannedRoutesResponse: (
      state,
      action: PayloadAction<PlannedRoute[]>
    ) => {

      // Copy to mutate objects
      state.plannedRoutes = action.payload.map(item => ({
        ...item,
        trail: null,
        isEnabled: false
      } as PlannedRouteExt));

      // Initialize enabled planned route
      const enabledPlannedRoute = getEnabledPlannedRoute(state.plannedRoutes);
      if (enabledPlannedRoute != null) {
        enabledPlannedRoute.isEnabled = true;
      }

      state.isLoadingPlannedRoutes = false;
    },
    savePlannedRoute: (state, action: PayloadAction<PlannedRouteExt>) => {
        if (state.editingPlannedRoute != null){
            const trackLength = state.editingPlannedRoute.trail?.trackLength || 0;
            const totalAscent = state.editingPlannedRoute.trail?.totalAscent || 0;
            const totalDescent = state.editingPlannedRoute.trail?.totalDescent || 0;

            state.editingPlannedRoute.routeLength = trackLength;
            state.editingPlannedRoute.totalAscent = totalAscent;
            state.editingPlannedRoute.totalDescent = totalDescent;
        }
        state.isSavingPlannedRoutes = true;
    },
    setSavingPlannedRouteResponse: (state, action: PayloadAction<{plannedRoute?: PlannedRoute | null | undefined}>) => {
      state.isSavingPlannedRoutes = false;
      if (state.editingPlannedRoute != null && action.payload.plannedRoute != null){

        state.editingPlannedRoute.id = action.payload.plannedRoute.id;
        state.editingPlannedRoute.uniqueId = action.payload.plannedRoute.uniqueId;
        state.editingPlannedRoute.routeName = action.payload.plannedRoute.routeName;

        if (state.plannedRoutes !== null) {
            // New route
            if (state.editingPlannedRoute.isNew){
                state.plannedRoutes = [...state.plannedRoutes, state.editingPlannedRoute];
                setPlannedRouteEnabled(state.editingPlannedRoute, state.plannedRoutes, true)
            }
            else{
              let statePlannedRoute = findPlannedRouteById(action.payload.plannedRoute.id, state.plannedRoutes)
              if (statePlannedRoute != null){
                // Update existing route
                Object.assign(statePlannedRoute, state.editingPlannedRoute);
              }
            }
        }

        state.editingPlannedRoute.isNew = false;
        state.editingPlannedRoute.isEdited = false;
      }
    },
    deletePlannedRoute: (state, action: PayloadAction<PlannedRouteExt>) => {
        state.isSavingPlannedRoutes = true;
    },
    setDeletePlannedRouteResponse: (state, action: PayloadAction<{result: boolean, deletedRoute?: PlannedRoute}>) => {
        state.isSavingPlannedRoutes = false;
        if  (action.payload.result && action.payload.deletedRoute != null && state.plannedRoutes != null){
            let statePlannedRoute = findPlannedRouteById(action.payload.deletedRoute.id, state.plannedRoutes)
            if (statePlannedRoute != null){
                state.plannedRoutes = state.plannedRoutes.filter(route => route.id !== action.payload.deletedRoute?.id);
            }
        }
    },
    setPlannedRouteEnabled: (
      state,
      action: PayloadAction<{ plannedRoute: PlannedRouteExt, isEnabled: boolean }>
    ) => {
      if (state.plannedRoutes !== null) {
        setPlannedRouteEnabled(action.payload.plannedRoute, state.plannedRoutes, action.payload.isEnabled)
      }
    },
    updatePlannedRouteInfo: (
      state,
      action: PayloadAction<{ plannedRoute: PlannedRouteExt, pins: PlannedRoutePinInfo[], trail: PlannedRouteTrail | null}>
    ) => {
      if (state.editingPlannedRoute != null){
        updatePlannedRouteInfoByTrail(state.editingPlannedRoute, action.payload.pins, action.payload.trail)
      }
      else{
        if (state.plannedRoutes !== null) {
          let statePlannedRoute = findPlannedRouteById(action.payload.plannedRoute.id, state.plannedRoutes)
          if (statePlannedRoute != null){
            updatePlannedRouteInfoByTrail(statePlannedRoute, action.payload.pins, action.payload.trail)
          }
        }
      }
    },
    renamePlannedRoute: (
      state,
      action: PayloadAction<{ plannedRoute: PlannedRouteExt, routeName: string }>
    ) => {
      if (state.editingPlannedRoute != null){
        state.editingPlannedRoute.routeName = action.payload.routeName;
        state.editingPlannedRoute.isEdited = true;
      }
      else{
        if (state.plannedRoutes !== null) {
          let statePlannedRoute = findPlannedRouteById(action.payload.plannedRoute.id, state.plannedRoutes)
          if (statePlannedRoute != null){
            statePlannedRoute.routeName = action.payload.routeName;
            statePlannedRoute.isEdited = true;
          }
        }
      }
    },
    changeDescriptionPlanningRoute: (
      state,
      action: PayloadAction<{ plannedRoute: PlannedRouteExt, description: string }>
    ) => {
      if (state.editingPlannedRoute != null){
        state.editingPlannedRoute.routeDescription = action.payload.description;
        state.editingPlannedRoute.isEdited = true;
      }
      else{
        if (state.plannedRoutes !== null) {
          let statePlannedRoute = findPlannedRouteById(action.payload.plannedRoute.id, state.plannedRoutes)
          if (statePlannedRoute != null){
            statePlannedRoute.routeDescription = action.payload.description;
            statePlannedRoute.isEdited = true;
          }
        }
      }
    },
    startEditingPlannedRoute: (
      state,
      action: PayloadAction<{ plannedRoute?: PlannedRouteExt, guide: GuideGeoExt }>
    ) => {
      if (state.editingPlannedRoute == null){
        // Edit
        if (action.payload.plannedRoute !== undefined) {
          let statePlannedRoute = findPlannedRouteById(action.payload.plannedRoute.id, state.plannedRoutes!)
          if (statePlannedRoute != null){
            state.editingPlannedRoute = statePlannedRoute;
            if (state.editingPlannedRoute.trail == null){
                const routeBuilder = new GeoRouteBuilder(action.payload.guide, state.editingPlannedRoute);
                const trail = routeBuilder.buildTrail();
                if (trail != null){
                  updatePlannedRouteInfoByTrail(state.editingPlannedRoute, routeBuilder.getPins(), trail);
                }
            }
          }
        }
        // New
        else{
          state.editingPlannedRoute = {
            id: "0", // ID is auto incremented after each change in pins
            uniqueId: action.payload.guide.guideId + "-" + state.userProfile?.primaryInfo.id + "-" + Date.now(),
            guideId: action.payload.guide.guideId,
          } as PlannedRouteExt;
          state.editingPlannedRoute.pins = [];
          state.editingPlannedRoute.pinsInfo = [];
          state.editingPlannedRoute.isNew = true;
        }
      }
    },
    endEditingPlannedRoute: (
      state,
      action: PayloadAction<boolean>
    ) => {
      if (action.payload && state.editingPlannedRoute !== null) {
        state.editingPlannedRoute.routeLength = state.editingPlannedRoute.trail?.trackLength || 0;
        state.editingPlannedRoute.totalAscent = state.editingPlannedRoute.trail?.totalAscent || 0;
        state.editingPlannedRoute.totalDescent = state.editingPlannedRoute.trail?.totalDescent || 0;
      }
      state.editingPlannedRoute = null;
    },
    addEditingPlannedRoutePin: (
      state,
      action: PayloadAction<{ point: TrailPoint, guide: GuideGeoExt }>
    ) => {
        const point = action.payload.point;

        const locationStr = point.latitude + ',' + point.longitude;
        if (state.editingPlannedRoute != null){

            if (!checkNewTrailPointAllowed(action.payload.guide, state.editingPlannedRoute, point)){
                return;
            }

            state.editingPlannedRoute.pins.push(locationStr);
            state.editingPlannedRoute.pinsInfo?.push({
                pin: locationStr,
                latitude: point.latitude,
                longitude: point.longitude,
                trailName: point.trail.trackName,
                distance: 0,
                trailPoint: null
            });
            state.editingPlannedRoute.isEdited = true;

            state.editingPlannedRoute.counter = (typeof state.editingPlannedRoute.counter === 'number')
              ? state.editingPlannedRoute.counter + 1
              : 1;

            const routeBuilder = new GeoRouteBuilder(action.payload.guide, state.editingPlannedRoute);
            const trail = routeBuilder.buildTrail();

            updatePlannedRouteInfoByTrail(state.editingPlannedRoute as PlannedRouteExt, routeBuilder.getPins(), trail);
        }
    },
    removeEditingPlannedRoutePin: (
      state,
      action: PayloadAction<{ pinsInfo : PlannedRoutePinInfo, guide: GuideGeoExt }>
    ) => {
        if (state.editingPlannedRoute != null){
            const pinStr = action.payload.pinsInfo.pin;
            state.editingPlannedRoute.pins = state.editingPlannedRoute.pins.filter(pin => pin !== pinStr);
            state.editingPlannedRoute.pinsInfo = state.editingPlannedRoute.pinsInfo?.filter(pin => pin.pin !== pinStr);
            state.editingPlannedRoute.isEdited = true;

            state.editingPlannedRoute.counter = (typeof state.editingPlannedRoute.counter === 'number')
              ? state.editingPlannedRoute.counter + 1
              : 1;

            const routeBuilder = new GeoRouteBuilder(action.payload.guide, state.editingPlannedRoute);
            const trail = routeBuilder.buildTrail();
            updatePlannedRouteInfoByTrail(state.editingPlannedRoute as PlannedRouteExt, routeBuilder.getPins(), trail);
        }
    },
    reverseDirectionPlanningRoute: (
      state,
      action: PayloadAction<{ guide: GuideGeoExt }>
    ) => {
        if (state.editingPlannedRoute != null){
            const reversedPins = state.editingPlannedRoute.pins.slice().reverse();
            const reversedPinsInfo = state.editingPlannedRoute.pinsInfo?.slice().reverse();

            state.editingPlannedRoute.pins = reversedPins;
            state.editingPlannedRoute.pinsInfo = reversedPinsInfo;
            state.editingPlannedRoute.isEdited = true;

            state.editingPlannedRoute.counter = (typeof state.editingPlannedRoute.counter === 'number')
              ? state.editingPlannedRoute.counter + 1
              : 1;

            const routeBuilder = new GeoRouteBuilder(action.payload.guide, state.editingPlannedRoute);
            const trail = routeBuilder.buildTrail();
            updatePlannedRouteInfoByTrail(state.editingPlannedRoute as PlannedRouteExt, routeBuilder.getPins(), trail);
        }
    },
  },
});

export const userActions = userSlice.actions;
export const userReducer = userSlice.reducer;